import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import { setQuantity, setColor, setFont, setFoilColor, setImage } from './napkinSlice';
import optionsConfig from './optionsConfig';
import './FanOutOptions.css';
import './metallicEffects.css';

interface FanOutOptionsProps {
  activeOption: 'napkin' | 'quantity' | 'text' | 'image' | 'test' | null;
  onClose: () => void;
}

const FanOutOptions: React.FC<FanOutOptionsProps> = ({ activeOption, onClose }) => {
  const dispatch = useDispatch();
  const napkinState = useSelector((state: RootState) => state.napkin);
  const [testItemCount, setTestItemCount] = React.useState(8);
  const [testRadius, setTestRadius] = React.useState(180);
  const [applyColorButton, setApplyColorButton] = React.useState(false);
  const [applyQuantityBtn, setApplyQuantityBtn] = React.useState(false);
  const [applyStackedButton, setApplyStackedButton] = React.useState(false);
  const [applyRingClass, setApplyRingClass] = React.useState(false);
  const [useInnerDiv, setUseInnerDiv] = React.useState(false);


  const handleQuantitySelect = (quantity: number) => {
    dispatch(setQuantity(quantity));
  };

  const handleColorSelect = (colorName: string) => {
    dispatch(setColor(colorName));
  };

    // Removed unused function - orientation is set directly in onclick handlers

  const handleFontChange = (fontName: string) => {
    dispatch(setFont(fontName));
  };

  const handleFoilColorSelect = (colorName: string) => {
    dispatch(setFoilColor(colorName));
  };

  const handleImageSelect = (imageName: string) => {
    dispatch(setImage(imageName));
  };

  // Debug metallic colors
  React.useEffect(() => {
    if (activeOption === 'text') {
      console.log('Text option active');
      console.log('Fonts:', optionsConfig.text.font);
      console.log('Foil colors:', optionsConfig.foil.color);
      console.log('Metallic colors:', optionsConfig.foil.color.filter(c => c.name.toLowerCase().includes('metallic')));
    }
  }, [activeOption]);

  if (!activeOption) return null;

  return (
    <div className="fan-out-container">
      <div className="fan-out-backdrop" onClick={onClose} />
      <div className="fan-out-centered-wrapper">
        {activeOption === 'napkin' && (
        <div 
          className="fan-out-options napkin-fan"
          style={{ '--total-options': optionsConfig.napkin.color.length } as React.CSSProperties}
        >
          {/* Only Colors in circular arrangement */}
          {optionsConfig.napkin.color.map((color, index) => (
            <div 
              key={color.name}
              className="fan-option napkin-color-option"
              style={{ 
                '--fan-index': index,
                backgroundColor: color.hex,
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: '3px solid rgba(255, 255, 255, 0.3)',
                cursor: 'pointer',
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } as React.CSSProperties}
              title={color.tooltip}
              onClick={() => handleColorSelect(color.name)}
            >
              {napkinState.color === color.name && (
                <div style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  width: '20px',
                  height: '20px',
                  // Counter-rotate to keep indicator upright
                  transform: `rotate(calc(-1 * ${index} * 360deg / ${optionsConfig.napkin.color.length}))`
                }}>
                  <div className="selected-indicator">✓</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeOption === 'quantity' && (
        <div 
          className="fan-out-options quantity-fan"
          style={{ '--total-options': optionsConfig.napkin.quantity.length } as React.CSSProperties}
        >
          {/* Quantity Options in circular arrangement */}
          {optionsConfig.napkin.quantity.map((qty, index) => (
            <div 
              key={qty}
              className="fan-option quantity-option-fan"
              style={{ 
                '--fan-index': index,
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              } as React.CSSProperties}
              onClick={() => handleQuantitySelect(qty)}
            >
              <div 
                className={`quantity-btn ${napkinState.quantity === qty ? 'selected' : ''}`}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: napkinState.quantity === qty ? '#007bff' : 'rgba(220, 220, 220, 0.9)',
                  color: napkinState.quantity === qty ? 'white' : '#333',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  // Counter-rotate to keep text upright
                  transform: `rotate(calc(-1 * var(--fan-index) * 360deg / var(--total-options)))`
                }}
              >
                {qty}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeOption === 'text' && (
        <div
          className="fan-out-options text-fan"
          style={{ '--total-options': optionsConfig.text.font.length + optionsConfig.foil.color.length } as React.CSSProperties}
        >
          {/* Debug: Total colors = {optionsConfig.foil.color.length}, Total fonts = {optionsConfig.text.font.length} */}
          {/* Fonts */}
          {optionsConfig.text.font.map((font, index) => (
            <div 
              key={font.name}
              className="fan-option font-option"
              style={{ 
                '--fan-index': index,
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } as React.CSSProperties}
              onClick={() => handleFontChange(font.name)}
            >
              <div 
                className={`font-btn ${napkinState.font === font.name ? 'selected' : ''}`}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: napkinState.font === font.name ? '#007bff' : 'rgba(220, 220, 220, 0.9)',
                  color: napkinState.font === font.name ? 'white' : '#333',
                  fontWeight: 'bold',
                  fontSize: '10px',
                  cursor: 'pointer',
                  // Counter-rotate to keep text upright
                  transform: `rotate(calc(-1 * var(--fan-index) * 360deg / var(--total-options)))`
                }}
              >
                {font.name}
              </div>
            </div>
          ))}
          
          {/* Foil Colors */}
          {optionsConfig.foil.color.map((color, index) => {
            const isMetallic = color.name.toLowerCase().includes('metallic');
            const metallicClass = isMetallic ? 'metallic' : '';
            const actualIndex = index + optionsConfig.text.font.length; // Continue from fonts
            
            // For metallic colors, we'll use gradient styles
            const getMetallicStyle = () => {
              // For now, just use the base color to make sure they're visible
              return { backgroundColor: color.hex };
            };
            
            return (
              <div 
                key={color.name}
                className={`fan-option foil-color-option ${metallicClass}`}
                style={{ 
                  '--fan-index': actualIndex,
                  ...getMetallicStyle(),
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isMetallic ? '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.3)' : undefined
                } as any}
                title={color.name}
                onClick={() => handleFoilColorSelect(color.name)}
              >
                {napkinState.foilColor === color.name && (
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    width: '20px',
                    height: '20px',
                    // Counter-rotate to keep indicator upright
                    transform: `rotate(calc(-1 * ${actualIndex} * 360deg / ${optionsConfig.text.font.length + optionsConfig.foil.color.length}))`
                  }}>
                    <div className="selected-indicator">✓</div>
                  </div>
                )}
                {/* Only show sparkle if not selected */}
                {isMetallic && napkinState.foilColor !== color.name && <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  fontSize: '12px',
                  animation: 'twinkle 2s ease-in-out infinite'
                }}>✨</span>}
              </div>
            );
          })}
        </div>
      )}

      {activeOption === 'image' && (
        <div 
          className="fan-out-options image-fan"
          style={{ '--total-options': optionsConfig.picture.images.length } as React.CSSProperties}
        >
          {optionsConfig.picture.images.map((image, index) => (
            <div 
              key={image.name}
              className="fan-option image-option-fan"
              style={{ 
                '--fan-index': index,
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } as React.CSSProperties}
              onClick={() => handleImageSelect(image.name)}
            >
              <div 
                className={`image-btn ${napkinState.image === image.name ? 'selected' : ''}`}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: napkinState.image === image.name ? '#007bff' : 'rgba(220, 220, 220, 0.9)',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  // Counter-rotate to keep images upright
                  transform: `rotate(calc(-1 * var(--fan-index) * 360deg / var(--total-options)))`
                }}
              >
                {image.name === 'a0' ? (
                  <span className="no-image" style={{ fontSize: '24px', color: '#999' }}>∅</span>
                ) : (
                  <img 
                    src={`/images/${image.name}.png`}
                    alt={image.name}
                    style={{
                      width: '80%',
                      height: '80%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `/src/Images/${image.name}.png`;
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeOption === 'test' && (
        <>
          {/* Controls */}
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              padding: '20px',
              borderRadius: '10px',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              minWidth: '300px',
              pointerEvents: 'auto'
            }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Number of items: {testItemCount}
              </label>
              <input 
                type="range" 
                min="1" 
                max="20" 
                value={testItemCount}
                onChange={(e) => setTestItemCount(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Radius: {testRadius}px
              </label>
              <input 
                type="range" 
                min="50" 
                max="300" 
                step="10"
                value={testRadius}
                onChange={(e) => setTestRadius(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <div>Angle between items: {(360 / testItemCount).toFixed(1)}°</div>
              <div>Circle circumference: {(2 * Math.PI * testRadius).toFixed(0)}px</div>
              <div>Space per item: {((2 * Math.PI * testRadius) / testItemCount).toFixed(0)}px</div>
            </div>
            
            <div style={{ borderTop: '1px solid #ccc', paddingTop: '15px', marginTop: '15px' }}>
              <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>CSS Class Tests:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={applyColorButton}
                    onChange={(e) => setApplyColorButton(e.target.checked)}
                  /> Apply .color-button class
                </label>
                <label style={{ fontSize: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={applyQuantityBtn}
                    onChange={(e) => setApplyQuantityBtn(e.target.checked)}
                  /> Apply .quantity-btn class
                </label>
                <label style={{ fontSize: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={applyStackedButton}
                    onChange={(e) => setApplyStackedButton(e.target.checked)}
                  /> Apply .stacked-button class
                </label>
                <label style={{ fontSize: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={applyRingClass}
                    onChange={(e) => setApplyRingClass(e.target.checked)}
                  /> Apply .ring-2 class
                </label>
                <label style={{ fontSize: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={useInnerDiv}
                    onChange={(e) => setUseInnerDiv(e.target.checked)}
                  /> Use inner div (like napkin)
                </label>
              </div>
            </div>
          </div>

          {/* Test Fan */}
          <div 
            className="fan-out-options test-fan"
            style={{ 
              '--total-options': testItemCount,
              '--test-radius': `${testRadius}px`,
              '--ring-2-total': testItemCount
            } as React.CSSProperties}
          >
            {Array.from({ length: testItemCount }, (_, index) => {
              const classes = [
                'fan-option',
                'test-option',
                applyColorButton && 'color-button',
                applyQuantityBtn && 'quantity-btn',
                applyStackedButton && 'stacked-button',
                applyRingClass && 'ring-2'
              ].filter(Boolean).join(' ');
              
              const content = useInnerDiv ? (
                <div 
                  className={applyQuantityBtn ? 'quantity-btn' : ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `hsl(${(360 / testItemCount) * index}, 70%, 50%)`,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {index + 1}
                </div>
              ) : (
                index + 1
              );
              
              return (
                <div 
                  key={index}
                  className={classes}
                  style={{ 
                    '--fan-index': index,
                    '--ring-index': index,
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: useInnerDiv ? undefined : `hsl(${(360 / testItemCount) * index}, 70%, 50%)`,
                    color: useInnerDiv ? undefined : 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    animation: applyRingClass ? 'napkinRing2 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards' : `testFanOut 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
                    animationDelay: `${index * 0.04}s`,
                    opacity: 0,
                    transform: 'scale(0)',
                    position: 'absolute'
                  } as React.CSSProperties}
                >
                  {content}
                </div>
              );
            })}
          </div>

          <style>{`
            @keyframes testFanOut {
              0% {
                opacity: 0;
                transform: scale(0);
              }
              100% {
                opacity: 1;
                transform: scale(1) rotate(calc(var(--fan-index) * 360deg / var(--total-options))) translateX(var(--test-radius));
              }
            }
          `}</style>
        </>
      )}
      </div>
    </div>
  );
};

export default FanOutOptions;
